import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Phone,
  Email,
  DirectionsCar as CarIcon,
  Business,
  Person,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle,
  Cancel as CancelIcon,
  SwapHoriz,
} from '@mui/icons-material';
import { insuranceClientService } from '../../services/insuranceClientService';
import { requestService, Request } from '../../services/requestService';
import { serviceUsageService } from '../../services/serviceUsageService';
import { serviceProviderService } from '../../services/serviceProviderService';
import CreateInvoiceModal from '../../components/CreateInvoiceModal';
import AddPaymentModal from '../../components/AddPaymentModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const InsuranceClientOverview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<any>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [viewingRequest, setViewingRequest] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningRequest, setAssigningRequest] = useState<Request | null>(null);
  const [providerToAssign, setProviderToAssign] = useState<any>(null);
  const [changeProviderOpen, setChangeProviderOpen] = useState(false);
  const [changingRequest, setChangingRequest] = useState<Request | null>(null);
  const [newProvider, setNewProvider] = useState<any>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [requestForInvoice, setRequestForInvoice] = useState<Request | null>(null);
  const [requestForPayment, setRequestForPayment] = useState<Request | null>(null);

  useEffect(() => {
    if (id) {
      loadData();
      loadServiceProviders();
    }
  }, [id]);

  const loadServiceProviders = async () => {
    try {
      const response = await serviceProviderService.getAll({
        'pagination[pageSize]': 1000,
        populate: '*'
      });
      const providers = response.data || [];
      // Sort: active first, then by name
      const sorted = providers.sort((a: any, b: any) => {
        const aActive = a.attributes?.isActive ?? a.isActive ?? false;
        const bActive = b.attributes?.isActive ?? b.isActive ?? false;
        if (aActive !== bActive) {
          return aActive ? -1 : 1;
        }
        const aName = a.attributes?.name || a.name || '';
        const bName = b.attributes?.name || b.name || '';
        return aName.localeCompare(bName);
      });
      setServiceProviders(sorted);
    } catch (error) {
      console.error('Error loading service providers:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load insurance client
      const clientResponse = await insuranceClientService.getOne(Number(id));
      setClient(clientResponse);
      
      // Debug: Log service usages
      const clientData = clientResponse.attributes || clientResponse;
      let serviceUsagesData = clientData.serviceUsages?.data || clientData.serviceUsages || [];
      
      // If serviceUsages are not loaded, try to fetch them separately
      if (!serviceUsagesData || serviceUsagesData.length === 0) {
        try {
          const serviceUsagesResponse = await serviceUsageService.getByClient(Number(id));
          serviceUsagesData = serviceUsagesResponse || [];
          console.log('Service Usages loaded separately:', serviceUsagesData);
        } catch (err) {
          console.warn('Could not load service usages separately:', err);
        }
      } else {
        console.log('Service Usages loaded from client:', serviceUsagesData);
      }

      // Load requests for this client
      const requestsResponse = await requestService.getAll({
        'filters[insuranceClient][id][$eq]': id,
        populate: {
          serviceProvider: { populate: '*' },
          invoice: { populate: '*' },
        },
        sort: 'createdAt:desc',
        'pagination[pageSize]': 1000,
      });
      setRequests(requestsResponse.data || []);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('Failed to load insurance client data.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: 'warning',
      assigned: 'info',
      active: 'primary',
      invoice_sent: 'secondary',
      payment_received: 'success',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status.toLowerCase()] || 'default';
  };

  const formatCurrency = (amount: number) => {
    return `OMR ${amount.toFixed(3)}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };

  const getStatusStep = (status: string) => {
    const steps: { [key: string]: number } = {
      pending: 0,
      assigned: 1,
      active: 2,
      invoice_sent: 3,
      payment_received: 4,
      completed: 5,
    };
    return steps[status.toLowerCase()] || 0;
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      pending: 'Pending',
      assigned: 'Assigned',
      active: 'Active',
      invoice_sent: 'Invoice Sent',
      payment_received: 'Payment Received',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status.toLowerCase()] || status;
  };

  const handleViewRequest = async (requestId: number) => {
    try {
      const response = await requestService.getById(requestId);
      setViewingRequest(response.data);
      setDetailsOpen(true);
    } catch (err: any) {
      console.error('Error loading request:', err);
      setError('Failed to load request details.');
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setViewingRequest(null);
  };

  // Helper function to format phone number for WhatsApp
  const formatPhoneForWhatsApp = (phone: string): string => {
    if (!phone) return '';
    let cleaned = phone.replace(/[^\d+]/g, '');
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  // Helper function to open WhatsApp chat
  const openWhatsAppChat = (phone: string) => {
    const formattedPhone = formatPhoneForWhatsApp(phone);
    if (formattedPhone) {
      window.open(`https://wa.me/${formattedPhone.replace('+', '')}`, '_blank');
    }
  };

  const handleAssignDialogOpen = (request: Request) => {
    setAssigningRequest(request);
    setProviderToAssign(null);
    setAssignDialogOpen(true);
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    setAssigningRequest(null);
    setProviderToAssign(null);
  };

  const handleAssignProvider = async () => {
    if (!assigningRequest || !providerToAssign) {
      setError('Please select a service provider');
      return;
    }

    try {
      await requestService.assignProvider(assigningRequest.id, providerToAssign.id);
      await loadData();
      if (detailsOpen && viewingRequest && viewingRequest.id === assigningRequest.id) {
        const response = await requestService.getById(viewingRequest.id);
        setViewingRequest(response.data);
      }
      handleAssignDialogClose();
    } catch (error: any) {
      console.error('Error assigning provider:', error);
      setError(error.response?.data?.error?.message || 'Failed to assign provider');
    }
  };

  const handleChangeProviderOpen = (request: Request) => {
    setChangingRequest(request);
    setNewProvider(null);
    setChangeProviderOpen(true);
  };

  const handleChangeProviderClose = () => {
    setChangeProviderOpen(false);
    setChangingRequest(null);
    setNewProvider(null);
  };

  const handleChangeProvider = async () => {
    if (!changingRequest || !newProvider) {
      setError('Please select a service provider');
      return;
    }

    const reason = prompt('Please provide reason for changing service provider:');
    if (!reason) return;

    try {
      await requestService.changeProvider(changingRequest.id, newProvider.id, reason);
      await loadData();
      if (detailsOpen && viewingRequest && viewingRequest.id === changingRequest.id) {
        const response = await requestService.getById(viewingRequest.id);
        setViewingRequest(response.data);
      }
      handleChangeProviderClose();
    } catch (error: any) {
      console.error('Error changing provider:', error);
      setError(error.response?.data?.error?.message || 'Failed to change provider');
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const updateData: any = { status: newStatus };
      
      if (newStatus === 'active') {
        const request = requests.find(r => r.id === id);
        const attrs = request?.attributes || request;
        if (!attrs?.assignedAt) {
          updateData.assignedAt = new Date().toISOString();
        }
      } else if (newStatus === 'invoice_sent') {
        updateData.invoiceSentAt = new Date().toISOString();
      } else if (newStatus === 'payment_received') {
        updateData.paymentReceivedAt = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updateData.completedDate = new Date().toISOString();
      } else if (newStatus === 'cancelled') {
        const reason = prompt('Please provide a reason for cancellation:');
        if (!reason) return;
        updateData.cancelReason = reason;
      }
      
      await requestService.update(id, updateData);
      await loadData();
      if (detailsOpen && viewingRequest && viewingRequest.id === id) {
        const response = await requestService.getById(id);
        setViewingRequest(response.data);
      }
    } catch (error: any) {
      console.error('Error updating request status:', error);
      setError(error.response?.data?.error?.message || 'Failed to update status');
    }
  };

  // Calculate statistics
  const stats = {
    totalRequests: requests.length,
    pendingRequests: requests.filter(r => {
      const status = (r.attributes || r).status;
      return status === 'pending';
    }).length,
    activeRequests: requests.filter(r => {
      const status = (r.attributes || r).status;
      return ['assigned', 'active', 'invoice_sent', 'payment_received'].includes(status);
    }).length,
    completedRequests: requests.filter(r => {
      const status = (r.attributes || r).status;
      return status === 'completed';
    }).length,
    totalRevenue: requests.reduce((sum, r) => {
      const invoice = (r.attributes || r).invoice?.data?.attributes || (r.attributes || r).invoice;
      const total = invoice?.total ? parseFloat(invoice.total) : 0;
      return sum + total;
    }, 0),
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !client) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Insurance client not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/insurance-clients')} sx={{ mt: 2 }}>
          Back to Insurance Clients
        </Button>
      </Container>
    );
  }

  const clientData = client.attributes || client;
  const insuranceCompany = clientData.insuranceCompany?.data?.attributes || clientData.insuranceCompany?.attributes || clientData.insuranceCompany;
  const servicePackage = clientData.servicePackage?.data?.attributes || clientData.servicePackage?.attributes || clientData.servicePackage;
  
  // Get service usages
  const serviceUsagesData = clientData.serviceUsages?.data || clientData.serviceUsages || [];
  let serviceUsages = Array.isArray(serviceUsagesData) ? serviceUsagesData : [];
  
  // Map serviceType to serviceCode for counting
  const SERVICE_TYPE_TO_CODE_MAP: { [key: string]: string } = {
    'Vehicle Towing': 'TOWING_SERVICE',
    'Towing': 'TOWING_SERVICE',
    'Battery Boost': 'BATTERY_BOOST',
    'Lockout Assistance': 'LOCKOUT_SERVICE',
    'Fuel Delivery': 'FUEL_DELIVERY'
  };
  
  // If serviceUsages don't exist but package does, create them from package
  if (serviceUsages.length === 0 && servicePackage && servicePackage.services) {
    const packageServices = Array.isArray(servicePackage.services) ? servicePackage.services : [];
    serviceUsages = packageServices.map((service: any) => ({
      serviceName: service.name,
      serviceCode: service.code,
      allowedCount: service.allowedCount || 1,
      usedCount: 0
    }));
  }
  
  // Calculate actual usage from requests
  const calculateUsageFromRequests = () => {
    const serviceUsageFromRequests: { [key: string]: number } = {};
    
    // Count usage per service code from requests
    requests.forEach((req: any) => {
      const reqAttrs = req.attributes || req;
      let serviceCode = reqAttrs.serviceCode;
      
      // If no serviceCode but has serviceType, try to map it
      if (!serviceCode && reqAttrs.serviceType) {
        serviceCode = SERVICE_TYPE_TO_CODE_MAP[reqAttrs.serviceType] || null;
      }
      
      if (serviceCode) {
        if (!serviceUsageFromRequests[serviceCode]) {
          serviceUsageFromRequests[serviceCode] = 0;
        }
        serviceUsageFromRequests[serviceCode]++;
      }
    });
    
    // Handle shared limit: if ANY shared service has a request, mark all as used
    const SHARED_LIMIT_SERVICES = ['BATTERY_BOOST', 'LOCKOUT_SERVICE', 'FUEL_DELIVERY'];
    const hasAnySharedServiceRequest = SHARED_LIMIT_SERVICES.some(code => 
      serviceUsageFromRequests[code] > 0
    );
    
    if (hasAnySharedServiceRequest) {
      // Mark all shared services as used (count = 1 for all)
      SHARED_LIMIT_SERVICES.forEach(code => {
        if (!serviceUsageFromRequests[code] || serviceUsageFromRequests[code] === 0) {
          serviceUsageFromRequests[code] = 1;
        }
      });
    }
    
    return serviceUsageFromRequests;
  };
  
  const serviceUsageFromRequests = calculateUsageFromRequests();
  const towingServiceCodes = ['TOWING_SERVICE', 'TOWING', 'TOW'];
  const towingStatus = clientData.towingStatus || 'none';
  
  // Calculate service usage with towingStatus adjustments
  const calculateServiceUsage = () => {
    return serviceUsages.map((usage: any) => {
      const usg = usage.attributes || usage;
      const serviceCode = usg.serviceCode;
      const usedFromRequests = serviceUsageFromRequests[serviceCode] || 0;
      const allowed = usg.allowedCount || 0;
      
      // For towing services, apply towingStatus adjustments
      const isTowingService = towingServiceCodes.some(code => 
        serviceCode?.toUpperCase() === code.toUpperCase()
      );
      
      let used = usedFromRequests;
      let remaining = allowed - used;
      
      if (isTowingService && towingStatus) {
        if (towingStatus === 'red') {
          // Red = reduce by 2 from remaining
          remaining = Math.max(0, remaining - 2);
        } else if (towingStatus === 'yellow') {
          // Yellow = reduce by 1 from remaining
          remaining = Math.max(0, remaining - 1);
        }
      }
      
      return {
        ...usg,
        serviceName: usg.serviceName || serviceCode,
        serviceCode: serviceCode,
        usedCount: used,
        calculatedRemaining: remaining,
        allowedCount: allowed,
        originalUsedCount: usg.usedCount || 0
      };
    });
  };
  
  const calculatedServiceUsages = calculateServiceUsage();
  
  // Calculate service package usage summary
  const calculateServicePackageUsage = () => {
    if (calculatedServiceUsages.length === 0) {
      return { total: 0, used: 0, remaining: 0 };
    }
    
    let totalAllowed = 0;
    let totalUsed = 0;
    let totalRemaining = 0;
    
    calculatedServiceUsages.forEach((usage: any) => {
      const allowed = usage.allowedCount || 0;
      const used = usage.usedCount || 0;
      const remaining = usage.calculatedRemaining || 0;
      totalAllowed += allowed;
      totalUsed += used;
      totalRemaining += remaining;
    });
    
    return {
      total: totalAllowed,
      used: totalUsed,
      remaining: totalRemaining
    };
  };
  
  const packageUsage = calculateServicePackageUsage();

  return (
    <>
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/insurance-clients')}
            sx={{ mb: 2 }}
          >
            Back to Insurance Clients
          </Button>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
              <CarIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" gutterBottom>
                  {clientData.customerName || 'N/A'}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {clientData.phoneNumber && (
                    <Chip
                      icon={<Phone />}
                      label={clientData.phoneNumber}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {clientData.email && (
                    <Chip
                      icon={<Email />}
                      label={clientData.email}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {clientData.vinNumber && (
                    <Chip
                      icon={<CarIcon />}
                      label={`VIN: ${clientData.vinNumber}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={clientData.isActive !== false ? 'Active' : 'Inactive'}
                    color={clientData.isActive !== false ? 'success' : 'default'}
                    size="small"
                  />
                </Stack>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Vehicle Information
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">VIN Number</Typography>
                    <Typography variant="body1" fontWeight="medium">{clientData.vinNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Plate Number</Typography>
                    <Typography variant="body1">{clientData.plateNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Car Make & Model</Typography>
                    <Typography variant="body1">
                      {clientData.carMake || ''} {clientData.carModel || ''} {clientData.carYear ? `(${clientData.carYear})` : ''}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Insurance Information
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Insurance Company</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {insuranceCompany?.name || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Policy Number</Typography>
                    <Typography variant="body1">{clientData.policyNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Package</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {servicePackage?.name || servicePackage?.code || '-'}
                        {servicePackage && calculatedServiceUsages.length > 0 && (
                          <Chip 
                            label={`${packageUsage.remaining} remaining`} 
                            size="small" 
                            color={packageUsage.remaining > 0 ? 'success' : 'error'}
                            sx={{ ml: 1.5, fontWeight: 600, fontSize: '0.75rem' }}
                          />
                        )}
                      </Typography>
                      {servicePackage && calculatedServiceUsages.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {packageUsage.used} of {packageUsage.total} services used
                        </Typography>
                      )}
                      {servicePackage && calculatedServiceUsages.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                          Service usage data not available
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {clientData.policyEndDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Policy End Date</Typography>
                      <Typography variant="body1">
                        {formatDate(clientData.policyEndDate)}
                        {new Date(clientData.policyEndDate) < new Date() && (
                          <Chip label="Expired" color="error" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Paper>

          {/* Statistics Cards */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {stats.totalRequests}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Requests
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="warning.main">
                    {stats.pendingRequests}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {stats.completedRequests}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(stats.totalRevenue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Stack>

          {/* Service Package Usage Details */}
          {servicePackage && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                Service Package Usage Details
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service Name</TableCell>
                      <TableCell>Service Code</TableCell>
                      <TableCell>Used</TableCell>
                      <TableCell>Allowed</TableCell>
                      <TableCell>Remaining</TableCell>
                      <TableCell>Last Used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calculatedServiceUsages.length > 0 ? (
                      calculatedServiceUsages.map((usage: any, idx: number) => {
                      const used = usage.usedCount || 0;
                      const allowed = usage.allowedCount || 0;
                      const remaining = usage.calculatedRemaining || 0;
                      
                      // Try to get last used date
                      let lastUsedDate = null;
                      if (usage.lastUsedDate) {
                        try {
                          lastUsedDate = new Date(usage.lastUsedDate);
                          if (isNaN(lastUsedDate.getTime())) lastUsedDate = null;
                        } catch (e) {
                          lastUsedDate = null;
                        }
                      } else if (usage.usageDates) {
                        try {
                          const dates = typeof usage.usageDates === 'string' ? JSON.parse(usage.usageDates) : usage.usageDates;
                          if (Array.isArray(dates) && dates.length > 0) {
                            const sortedDates = dates
                              .map((d: any) => new Date(d))
                              .filter((d: Date) => !isNaN(d.getTime()))
                              .sort((a: Date, b: Date) => b.getTime() - a.getTime());
                            if (sortedDates.length > 0) {
                              lastUsedDate = sortedDates[0];
                            }
                          }
                        } catch (e) {
                          // Ignore parsing errors
                        }
                      }
                      
                      // Find the most recent request for this service to get last used date
                      if (!lastUsedDate) {
                        const serviceCode = usage.serviceCode;
                        const serviceRequests = requests
                          .filter((req: any) => {
                            const reqAttrs = req.attributes || req;
                            let reqServiceCode = reqAttrs.serviceCode;
                            if (!reqServiceCode && reqAttrs.serviceType) {
                              reqServiceCode = SERVICE_TYPE_TO_CODE_MAP[reqAttrs.serviceType] || null;
                            }
                            return reqServiceCode === serviceCode;
                          })
                          .sort((a: any, b: any) => {
                            const aDate = new Date((a.attributes || a).createdAt || 0);
                            const bDate = new Date((b.attributes || b).createdAt || 0);
                            return bDate.getTime() - aDate.getTime();
                          });
                        
                        if (serviceRequests.length > 0) {
                          const mostRecent = serviceRequests[0];
                          const reqAttrs = mostRecent.attributes || mostRecent;
                          if (reqAttrs.createdAt) {
                            lastUsedDate = new Date(reqAttrs.createdAt);
                          }
                        }
                      }
                      
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{usage.serviceName || '-'}</TableCell>
                          <TableCell>{usage.serviceCode || '-'}</TableCell>
                          <TableCell>
                            <Chip label={used} color={used > 0 ? 'warning' : 'default'} size="small" />
                          </TableCell>
                          <TableCell>{allowed}</TableCell>
                          <TableCell>
                            <Chip 
                              label={remaining} 
                              color={remaining > 0 ? 'success' : 'error'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            {lastUsedDate ? formatDate(lastUsedDate.toISOString()) : 'Never'}
                          </TableCell>
                        </TableRow>
                      );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Alert severity="info" sx={{ my: 2 }}>
                            No service usage records found. Service usage will be created automatically when services are used.
                          </Alert>
                          {servicePackage?.services && Array.isArray(servicePackage.services) && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Package Services:
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {servicePackage.services.map((service: any, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={`${service.name} (${service.allowedCount || 0} allowed)`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tabs */}
          <Paper>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label="Requests" />
            </Tabs>

            <TabPanel index={0} value={tabValue}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Request Number</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Service Provider</TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No requests found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      requests.map((request) => {
                        const reqAttrs = request.attributes || request;
                        const serviceProvider = reqAttrs.serviceProvider?.data?.attributes || reqAttrs.serviceProvider;
                        const invoice = reqAttrs.invoice?.data?.attributes || reqAttrs.invoice;
                        return (
                          <TableRow key={request.id}>
                            <TableCell>{reqAttrs.requestNumber || `#${request.id}`}</TableCell>
                            <TableCell>{reqAttrs.serviceType || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={reqAttrs.status || 'pending'}
                                color={getStatusColor(reqAttrs.status || 'pending') as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {serviceProvider?.name || '-'}
                            </TableCell>
                            <TableCell>{formatDate(reqAttrs.createdAt)}</TableCell>
                            <TableCell>
                              {invoice?.total ? formatCurrency(parseFloat(invoice.total)) : '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => handleViewRequest(request.id)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </Box>
      </Container>

      {/* Request Details Modal */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" fontWeight="bold">Request Details</Typography>
                {viewingRequest && (viewingRequest.imported || viewingRequest.attributes?.imported) && (
                  <Chip 
                    label="Imported" 
                    size="small"
                    sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.75rem',
                      bgcolor: 'grey.200',
                      color: 'grey.700',
                      height: '24px'
                    }}
                  />
                )}
              </Box>
              {viewingRequest && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Request #{viewingRequest.requestNumber || viewingRequest.attributes?.requestNumber || `REQ-${viewingRequest.id}`}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseDetails}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewingRequest && (
            <Stack spacing={3}>
              {/* Workflow Stepper */}
              <Card variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Workflow Progress
                </Typography>
                <Stepper 
                  activeStep={getStatusStep(viewingRequest.attributes?.status || viewingRequest.status || 'pending')} 
                  alternativeLabel
                  sx={{ mb: 2 }}
                >
                  <Step><StepLabel>Pending</StepLabel></Step>
                  <Step><StepLabel>Assigned</StepLabel></Step>
                  <Step><StepLabel>Active</StepLabel></Step>
                  <Step><StepLabel>Invoice Sent</StepLabel></Step>
                  <Step><StepLabel>Payment Received</StepLabel></Step>
                  <Step><StepLabel>Completed</StepLabel></Step>
                </Stepper>
                <Alert 
                  severity={
                    (viewingRequest.status === 'completed' || viewingRequest.attributes?.status === 'completed') ? 'success' :
                    (viewingRequest.status === 'cancelled' || viewingRequest.attributes?.status === 'cancelled') ? 'error' :
                    (viewingRequest.status === 'pending' || viewingRequest.attributes?.status === 'pending') ? 'info' : 'success'
                  } 
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Current Status:</strong> {getStatusLabel(viewingRequest.status || viewingRequest.attributes?.status || 'pending')}
                  </Typography>
                </Alert>
              </Card>

              {/* Request Information */}
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Request Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip 
                        label={getStatusLabel(viewingRequest.status || viewingRequest.attributes?.status || 'pending')} 
                        color={getStatusColor(viewingRequest.status || viewingRequest.attributes?.status || 'pending') as any} 
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {viewingRequest.description || viewingRequest.attributes?.description || 'No description provided'}
                    </Typography>
                  </Box>
                  {viewingRequest.serviceType || viewingRequest.attributes?.serviceType ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Service Type</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {viewingRequest.serviceType || viewingRequest.attributes?.serviceType}
                      </Typography>
                    </Box>
                  ) : null}
                  {viewingRequest.createdAt || viewingRequest.attributes?.createdAt ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Created Date</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {formatDate(viewingRequest.createdAt || viewingRequest.attributes?.createdAt)}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Card>

              {/* Service Provider Information */}
              {(viewingRequest.serviceProvider || viewingRequest.attributes?.serviceProvider) && (
                <Card variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    Service Provider
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Provider Name</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {viewingRequest.serviceProvider?.data?.attributes?.name || 
                         viewingRequest.attributes?.serviceProvider?.data?.attributes?.name || 
                         viewingRequest.serviceProvider?.name || 
                         viewingRequest.attributes?.serviceProvider?.name || 
                         'N/A'}
                      </Typography>
                    </Box>
                    {(viewingRequest.serviceProvider?.data?.attributes?.phone || 
                      viewingRequest.attributes?.serviceProvider?.data?.attributes?.phone) && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {viewingRequest.serviceProvider?.data?.attributes?.phone || 
                           viewingRequest.attributes?.serviceProvider?.data?.attributes?.phone}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Card>
              )}

              {/* WhatsApp Chat Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexDirection: { xs: 'column', sm: 'row' } 
              }}>
                {(viewingRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber || 
                  viewingRequest.attributes?.customer?.data?.attributes?.phone ||
                  viewingRequest.insuranceClient?.data?.attributes?.phoneNumber || 
                  viewingRequest.customer?.data?.attributes?.phone) && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => {
                      const phone = viewingRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber || 
                                   viewingRequest.attributes?.customer?.data?.attributes?.phone ||
                                   viewingRequest.insuranceClient?.data?.attributes?.phoneNumber || 
                                   viewingRequest.customer?.data?.attributes?.phone;
                      if (phone) {
                        openWhatsAppChat(phone);
                      }
                    }}
                    fullWidth
                    sx={{ 
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#20BA5A' }
                    }}
                  >
                    Chat with Client
                  </Button>
                )}
                {(viewingRequest.attributes?.serviceProvider?.data?.attributes?.phone || 
                  viewingRequest.serviceProvider?.data?.attributes?.phone) && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => {
                      const phone = viewingRequest.attributes?.serviceProvider?.data?.attributes?.phone || 
                                   viewingRequest.serviceProvider?.data?.attributes?.phone;
                      if (phone) {
                        openWhatsAppChat(phone);
                      }
                    }}
                    fullWidth
                    sx={{ 
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#20BA5A' }
                    }}
                  >
                    Chat with Service Provider
                  </Button>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button onClick={handleCloseDetails} color="inherit">
            Close
          </Button>
          {viewingRequest && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* Assign button for pending requests */}
              {(viewingRequest.attributes?.status === 'pending' || viewingRequest.status === 'pending') && 
               !(viewingRequest.attributes?.serviceProvider?.data || viewingRequest.serviceProvider?.data) && (
                <Button
                  onClick={() => handleAssignDialogOpen(viewingRequest)}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                >
                  Assign Service Provider
                </Button>
              )}
              
              {/* Workflow progression buttons */}
              {(viewingRequest.attributes?.status === 'assigned' || viewingRequest.status === 'assigned') && (
                <Button
                  onClick={async () => {
                    await handleStatusChange(viewingRequest.id, 'active');
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                >
                  Start Work (Mark Active)
                </Button>
              )}
              {(viewingRequest.attributes?.status === 'active' || viewingRequest.status === 'active') && (
                <Button
                  onClick={() => {
                    setRequestForInvoice(viewingRequest);
                    setInvoiceModalOpen(true);
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                >
                  Send Invoice
                </Button>
              )}
              {(viewingRequest.attributes?.status === 'invoice_sent' || viewingRequest.status === 'invoice_sent') && (
                <Button
                  onClick={() => {
                    setRequestForPayment(viewingRequest);
                    setPaymentModalOpen(true);
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                >
                  Confirm Payment
                </Button>
              )}
              {(viewingRequest.attributes?.status === 'payment_received' || viewingRequest.status === 'payment_received') && (
                <Button
                  onClick={async () => {
                    await handleStatusChange(viewingRequest.id, 'completed');
                    handleCloseDetails();
                  }}
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                >
                  Complete Request
                </Button>
              )}

              {/* Change provider button for active requests */}
              {(viewingRequest.attributes?.status !== 'pending' && 
                viewingRequest.attributes?.status !== 'completed' && 
                viewingRequest.attributes?.status !== 'cancelled' &&
                viewingRequest.status !== 'pending' && 
                viewingRequest.status !== 'completed' && 
                viewingRequest.status !== 'cancelled') && (
                <Button
                  onClick={() => handleChangeProviderOpen(viewingRequest)}
                  variant="outlined"
                  color="warning"
                  startIcon={<SwapHoriz />}
                >
                  Change Provider
                </Button>
              )}
              
              {/* Cancel button for non-completed requests */}
              {(viewingRequest.attributes?.status !== 'completed' && 
                viewingRequest.attributes?.status !== 'cancelled' &&
                viewingRequest.status !== 'completed' && 
                viewingRequest.status !== 'cancelled') && (
                <Button
                  onClick={async () => {
                    await handleStatusChange(viewingRequest.id, 'cancelled');
                    handleCloseDetails();
                  }}
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                >
                  Cancel Request
                </Button>
              )}
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Assign Provider Dialog */}
      <Dialog open={assignDialogOpen} onClose={handleAssignDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Service Provider</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request</Typography>
              <Typography variant="body1">
                {assigningRequest?.attributes?.requestNumber || assigningRequest?.requestNumber || `REQ-${assigningRequest?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assigningRequest?.attributes?.description || assigningRequest?.description}
              </Typography>
            </Box>
            <Autocomplete
              fullWidth
              options={serviceProviders}
              value={providerToAssign}
              onChange={(_, newValue) => setProviderToAssign(newValue)}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option;
                return attrs.name || `Provider ${option.id}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Service Provider *"
                  placeholder="Search provider..."
                  required
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option;
                const isActive = attrs.isActive ?? true;
                
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                          {attrs.name}
                        </Typography>
                        <Chip 
                          label={isActive ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={isActive ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {attrs.type} - {attrs.phone}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No service providers found"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button onClick={handleAssignProvider} variant="contained" color="success">
            Assign Provider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Provider Dialog */}
      <Dialog open={changeProviderOpen} onClose={handleChangeProviderClose} maxWidth="sm" fullWidth>
        <DialogTitle>Change Service Provider</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request</Typography>
              <Typography variant="body1">
                {changingRequest?.attributes?.requestNumber || changingRequest?.requestNumber || `REQ-${changingRequest?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {changingRequest?.attributes?.description || changingRequest?.description}
              </Typography>
            </Box>
            {(changingRequest?.attributes?.serviceProvider?.data?.attributes || changingRequest?.serviceProvider?.data?.attributes) && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Current Provider</Typography>
                <Typography variant="body1">
                  {changingRequest.attributes?.serviceProvider?.data?.attributes?.name || 
                   changingRequest.serviceProvider?.data?.attributes?.name}
                </Typography>
              </Box>
            )}
            <Autocomplete
              fullWidth
              options={serviceProviders}
              value={newProvider}
              onChange={(_, newValue) => setNewProvider(newValue)}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option;
                return attrs.name || `Provider ${option.id}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select New Service Provider *"
                  placeholder="Search provider..."
                  required
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option;
                const isActive = attrs.isActive ?? true;
                
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                          {attrs.name}
                        </Typography>
                        <Chip 
                          label={isActive ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={isActive ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {attrs.type} - {attrs.phone}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No service providers found"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangeProviderClose}>Cancel</Button>
          <Button onClick={handleChangeProvider} variant="contained" color="primary">
            Change Provider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Invoice Modal */}
      {requestForInvoice && (
        <CreateInvoiceModal
          open={invoiceModalOpen}
          onClose={() => {
            setInvoiceModalOpen(false);
            setRequestForInvoice(null);
          }}
          request={requestForInvoice}
          onSuccess={() => {
            loadData();
            handleCloseDetails();
          }}
        />
      )}

      {/* Add Payment Modal */}
      {requestForPayment && (
        <AddPaymentModal
          open={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setRequestForPayment(null);
          }}
          request={requestForPayment}
          onSuccess={() => {
            loadData();
            handleCloseDetails();
          }}
        />
      )}
    </>
  );
};

export default InsuranceClientOverview;

